import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Layers, Sparkles, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { 
  useUserCloud, 
  EmailCaptureModal,
  EmergentCTA,
  AuthorCard,
  resourceColors as colors 
} from '../ResourceComponents';

const resource = {
  id: 'personal-brand-tech-stack',
  type: 'stack',
  title: 'Personal Brand Tech Stack',
  description: 'The exact tools I use to build and manage my personal brand online.',
  color: '#6A0DAD',
  author: {
    name: 'techbaenae',
    handle: '@techbaenae',
    avatar: 'https://customer-assets.emergentagent.com/job_e02576fc-3e7a-4446-bc21-d558624baf1e/artifacts/zhpzfvij_naomi%20headshot%20zoomed%20in.jpg',
    title: 'Data Dreamer'
  },
};

// Tech stack apps with their logos and descriptions
const techStack = [
  {
    name: 'Emergent',
    category: 'Website Builder',
    description: 'AI-powered website builder. Build full-stack apps with prompts.',
    color: '#000000',
    logo: 'E',
    gradient: 'linear-gradient(135deg, #1a1a1a 0%, #333 100%)',
    link: 'https://app.emergent.sh/?via=naomi',
    featured: true,
  },
  {
    name: 'Notion',
    category: 'Operations',
    description: 'Second brain for notes, tasks, databases, and project management.',
    color: '#000000',
    logo: 'N',
    gradient: 'linear-gradient(135deg, #fff 0%, #f5f5f5 100%)',
    textDark: true,
    link: 'https://notion.so',
  },
  {
    name: 'Airtable',
    category: 'Data',
    description: 'Spreadsheet-database hybrid for tracking everything.',
    color: '#18BFFF',
    logo: 'A',
    gradient: 'linear-gradient(135deg, #18BFFF 0%, #2D7FF9 100%)',
    link: 'https://airtable.com',
  },
  {
    name: 'Planable',
    category: 'Content Scheduling',
    description: 'Plan, collaborate, and schedule social media content.',
    color: '#6366F1',
    logo: 'P',
    gradient: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
    link: 'https://planable.io',
  },
  {
    name: 'Canva',
    category: 'Design',
    description: 'Quick graphics, social posts, presentations, and brand kits.',
    color: '#00C4CC',
    logo: 'C',
    gradient: 'linear-gradient(135deg, #00C4CC 0%, #7B2FF7 100%)',
    link: 'https://canva.com',
  },
  {
    name: 'Beehiiv',
    category: 'Email Marketing',
    description: 'Newsletter platform built for growth and monetization.',
    color: '#FCD34D',
    logo: 'B',
    gradient: 'linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%)',
    link: 'https://beehiiv.com',
  },
  {
    name: 'Figma',
    category: 'UI Design',
    description: 'Collaborative design tool for mockups and prototypes.',
    color: '#F24E1E',
    logo: 'F',
    gradient: 'linear-gradient(135deg, #F24E1E 0%, #A259FF 100%)',
    link: 'https://figma.com',
  },
  {
    name: 'Cal.com',
    category: 'Scheduling',
    description: 'Open-source Calendly alternative for booking calls.',
    color: '#292929',
    logo: 'C',
    gradient: 'linear-gradient(135deg, #292929 0%, #525252 100%)',
    link: 'https://cal.com',
  },
];

// Apple-style app icon component
const AppIcon = ({ app, index }) => (
  <motion.a
    href={app.link}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * 0.05 }}
    className="group flex flex-col items-center gap-2"
  >
    <div 
      className={`w-20 h-20 rounded-[22px] flex items-center justify-center text-2xl font-bold shadow-lg transition-transform group-hover:scale-110 ${app.textDark ? 'text-gray-900' : 'text-white'} ${app.featured ? 'ring-2 ring-purple-500 ring-offset-2' : ''}`}
      style={{ background: app.gradient }}
    >
      {app.logo}
    </div>
    <span className="text-xs text-gray-600 font-medium text-center max-w-[80px] truncate">{app.name}</span>
  </motion.a>
);

// Stack category section
const StackCategory = ({ title, apps }) => (
  <div className="mb-12">
    <h3 className="text-lg font-semibold text-gray-900 mb-6">{title}</h3>
    <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-6">
      {apps.map((app, i) => (
        <AppIcon key={app.name} app={app} index={i} />
      ))}
    </div>
  </div>
);

// Detailed app card
const AppCard = ({ app }) => (
  <motion.a
    href={app.link}
    target="_blank"
    rel="noopener noreferrer"
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="flex items-start gap-4 p-4 rounded-2xl border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all group"
  >
    <div 
      className={`w-14 h-14 rounded-[16px] flex items-center justify-center text-xl font-bold flex-shrink-0 ${app.textDark ? 'text-gray-900' : 'text-white'}`}
      style={{ background: app.gradient }}
    >
      {app.logo}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <h4 className="font-semibold text-gray-900">{app.name}</h4>
        <ExternalLink className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      </div>
      <p className="text-xs text-purple-600 font-medium mb-1">{app.category}</p>
      <p className="text-sm text-gray-600">{app.description}</p>
    </div>
  </motion.a>
);

const TechStackPage = () => {
  const { userEmail, saveUser, saveResource } = useUserCloud();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (userEmail) {
      setHasAccess(true);
      saveResource(resource.id);
    }
  }, [userEmail]);

  const handleSaveToCloud = async (email) => {
    saveUser(email);
    saveResource(resource.id);
    setShowEmailModal(false);
    setHasAccess(true);
  };

  if (!hasAccess && !userEmail) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center">
          <div 
            className="w-24 h-24 rounded-3xl mx-auto mb-6 flex items-center justify-center"
            style={{ background: `${resource.color}20` }}
          >
            <Layers className="w-12 h-12" style={{ color: resource.color }} />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-gray-600 mb-8">{resource.description}</p>
          
          <button
            onClick={() => setShowEmailModal(true)}
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-medium text-white transition-all hover:opacity-90"
            style={{ background: colors.teal }}
          >
            <Sparkles className="w-5 h-5" />
            Save to Your Cloud
          </button>
          
          <p className="text-gray-400 text-sm mt-4">
            Enter your email to access this resource
          </p>

          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-gray-500 hover:text-gray-700 mt-8 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Data Dreaming
          </Link>
        </div>

        <EmailCaptureModal
          isOpen={showEmailModal}
          onClose={() => setShowEmailModal(false)}
          onSubmit={handleSaveToCloud}
          resourceTitle={resource.title}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm">Back</span>
          </Link>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Resource by</span>
            <span className="text-sm font-medium text-gray-900">{resource.author.handle}</span>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div 
        className="py-16 px-4"
        style={{ background: `linear-gradient(180deg, ${resource.color}10 0%, transparent 100%)` }}
      >
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <span 
              className="px-3 py-1 rounded-full text-xs font-medium"
              style={{ background: `${resource.color}20`, color: resource.color }}
            >
              Tech Stack
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">{resource.title}</h1>
          <p className="text-xl text-gray-600 max-w-2xl">{resource.description}</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        
        {/* Apple-style Home Screen */}
        <div className="mb-16 p-8 rounded-3xl bg-gradient-to-b from-gray-50 to-white border border-gray-100">
          <div className="text-center mb-8">
            <p className="text-sm text-gray-500 mb-2">My Home Screen</p>
            <h2 className="text-2xl font-bold text-gray-900">The Stack</h2>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6">
            {techStack.map((app, i) => (
              <AppIcon key={app.name} app={app} index={i} />
            ))}
          </div>

          <p className="text-center text-xs text-gray-400 mt-8">
            Tap any app to learn more
          </p>
        </div>

        {/* Detailed breakdown */}
        <h2 className="text-2xl font-bold text-gray-900 mb-6">The Breakdown</h2>
        <p className="text-gray-600 mb-8">
          Here's what each tool does in my personal brand workflow:
        </p>

        <div className="grid md:grid-cols-2 gap-4 mb-12">
          {techStack.map((app) => (
            <AppCard key={app.name} app={app} />
          ))}
        </div>

        <EmergentCTA />

        {/* How it all connects */}
        <div className="mt-12 p-6 rounded-2xl bg-gray-50">
          <h3 className="font-bold text-gray-900 mb-4">How It All Connects</h3>
          <div className="space-y-3 text-sm text-gray-600">
            <p><strong>Content Creation:</strong> Ideas in Notion → Design in Canva → Schedule in Planable</p>
            <p><strong>Website:</strong> Build with Emergent → Track with built-in analytics</p>
            <p><strong>Audience:</strong> Newsletter in Beehiiv → Booking via Cal.com</p>
            <p><strong>Data:</strong> Everything tracked in Airtable for insights</p>
          </div>
        </div>

        <AuthorCard author={resource.author} />
      </div>
    </div>
  );
};

export default TechStackPage;
